import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Chip, Stack, Typography } from "@mui/material";

import BackgroundImage from "../../../shared/image/placeholder.jpg";
import { insightsDesignEnabled } from "../../../common/utils/insightsDesignEnabled";
import {
  insightsImageCardImageFrameSx,
  insightsImageCardSurfaceSx,
  insightsImageCardTextSectionSx,
  insightsImageCardTitleSx,
  insightsMetricChipSx,
} from "./insightsComponentSx";

function MetricChip({ label, value, size = "small", variant = "default" }) {
  const chipLabel = label ? `${label}: ${value}` : value;
  const chipSx = insightsMetricChipSx({ size, variant });
  const isPrimary = variant === "primary";

  return (
    <Chip
      label={chipLabel}
      size={size}
      variant={!insightsDesignEnabled() && isPrimary ? "outlined" : "filled"}
      sx={chipSx}
    />
  );
}

MetricChip.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["small", "medium"]),
  variant: PropTypes.oneOf(["default", "primary"]),
};

/**
 * DS image card for Insights aggregate / flyer gallery.
 * Does not replace global components/ImageCard.
 */
export default function InsightsImageCard({
  title,
  description,
  imageUrl,
  placeholderImageUrl,
  imageFit = "cover",
  primaryMetric,
  primaryMetricPlacement = "overlay",
  metaChips = [],
  footer = null,
  onActivate,
  activateAriaLabel,
}) {
  const [imageSource, setImageSource] = useState(imageUrl || placeholderImageUrl || BackgroundImage);

  useEffect(() => {
    setImageSource(imageUrl || placeholderImageUrl || BackgroundImage);
  }, [imageUrl, placeholderImageUrl]);

  const visibleMetaChips = (metaChips || []).filter(
    (chip) => chip?.value != null && String(chip.value).trim() !== "" && chip.value !== "—"
  );

  const showPrimaryMetric =
    primaryMetric?.value != null && String(primaryMetric.value).trim() !== "";
  const showPrimaryMetricOverlay = showPrimaryMetric && primaryMetricPlacement === "overlay";
  const showPrimaryMetricInText = showPrimaryMetric && primaryMetricPlacement === "text";

  const descriptionText =
    description != null && String(description).trim() !== "" && description !== "—"
      ? String(description).trim()
      : null;

  const textSectionSx = insightsImageCardTextSectionSx();
  const textContent = (
    <>
      {title ? (
        <Typography component="p" sx={insightsImageCardTitleSx()}>
          {title}
        </Typography>
      ) : null}

      {descriptionText ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.3 }}>
          {descriptionText}
        </Typography>
      ) : null}

      {showPrimaryMetricInText ? (
        <>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.2 }}>
            {primaryMetric.value}
          </Typography>
        </>
      ) : null}

      {visibleMetaChips.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
          {visibleMetaChips.map((chip) => (
            <MetricChip
              key={`${chip.label || "chip"}-${chip.value}`}
              label={chip.label}
              value={chip.value}
            />
          ))}
        </Stack>
      ) : null}
    </>
  );

  const cardBody = (
    <>
      <Box sx={insightsImageCardImageFrameSx()}>
        <Box
          component="img"
          src={imageSource}
          alt=""
          loading="lazy"
          onError={() => setImageSource(placeholderImageUrl || BackgroundImage)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: imageFit,
            objectPosition: "center",
          }}
        />
        {showPrimaryMetricOverlay ? (
          <Box
            sx={{
              position: "absolute",
              left: 8,
              bottom: 8,
              maxWidth: "calc(100% - 16px)",
            }}
          >
            <MetricChip
              label={primaryMetric.label}
              value={primaryMetric.value}
              size="medium"
              variant="primary"
            />
          </Box>
        ) : null}
      </Box>

      {insightsDesignEnabled() ? textContent : <Box sx={textSectionSx}>{textContent}</Box>}
    </>
  );

  return (
    <Box
      sx={{
        ...insightsImageCardSurfaceSx(),
        ...(footer
          ? {
              display: "flex",
              flexDirection: "column",
              pb: 0,
            }
          : {}),
      }}
    >
      {onActivate ? (
        <Box
          role="button"
          tabIndex={0}
          aria-label={activateAriaLabel || "Open details"}
          onClick={onActivate}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onActivate();
            }
          }}
          sx={{
            cursor: "pointer",
            flex: 1,
            minWidth: 0,
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
            },
          }}
        >
          {cardBody}
        </Box>
      ) : (
        cardBody
      )}
      {footer ? <Box sx={{ mt: 1.5, mx: -2 }}>{footer}</Box> : null}
    </Box>
  );
}

InsightsImageCard.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  placeholderImageUrl: PropTypes.string,
  imageFit: PropTypes.oneOf(["cover", "contain"]),
  primaryMetric: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  primaryMetricPlacement: PropTypes.oneOf(["overlay", "text"]),
  metaChips: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  footer: PropTypes.node,
  onActivate: PropTypes.func,
  activateAriaLabel: PropTypes.string,
};
